/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file Connector Management filter definitions
 * @module Epic.AppOrchard.ConnectorManagement.ColumnDefinitions
 */

import {
	IdColumn,
	LinksColumn,
	NameColumn,
	OrganizationSourceColumn,
	OrganizationStatusColumn,
	SlgIdColumn,
	USCDIOrganizationStatusColumn,
} from "ao/components/OrganizationManagement/Shared/data/columns";
import { IColumnDefinition } from "ao/types";
import { IConnectorAppListItem } from "ao/types/connectorManagement";
import { IConnectorsOrganizationListItem } from "ao/types/organizationManagement";
import { getStringForOrganizationStatus } from "ao/utils/helpers";
import { AppListColumnKey, OrgListConnectorsColumnKey } from "./constants";

export const AppListColumnDefs: IColumnDefinition[] = [
	{
		key: AppListColumnKey.id,
		name: "Connector App ID",
		tooltip: "Connector App ID",
		defaultShow: true,
		isSortable: true,
		minWidth: 100,
		maxWidth: 155,
		width: 100,
	},
	{
		key: AppListColumnKey.trackId,
		name: "IVP ID",
		tooltip: "App Track ID",
		defaultShow: false,
		isSortable: true,
		minWidth: 100,
		maxWidth: 100,
		width: 100,
	},
	{
		key: AppListColumnKey.clientAppId,
		name: "Client App ID",
		tooltip: "Client App ID",
		defaultShow: true,
		isSortable: true,
		minWidth: 100,
		maxWidth: 140,
		width: 100,
	},
	{
		key: AppListColumnKey.name,
		name: "Name",
		tooltip: "App Name",
		defaultShow: true,
		isSortable: true,
		width: 175,
		minWidth: 175,
		maxWidth: 300,
	},
	{
		key: AppListColumnKey.version,
		name: "Version",
		tooltip: "App Version",
		defaultShow: true,
		isSortable: true,
		minWidth: 100,
		maxWidth: 100,
		width: 100,
	},
	{
		key: AppListColumnKey.appCategories,
		name: "App Categories",
		tooltip: "App Categories",
		defaultShow: true,
		isSortable: false,
		width: 175,
		minWidth: 175,
		maxWidth: 350,
	},
	{
		key: AppListColumnKey.reviewStatusName,
		name: "Review Status",
		tooltip: "Review Status of App",
		defaultShow: false,
		isSortable: true,
		minWidth: 140,
		maxWidth: 140,
		width: 140,
	},
	{
		key: AppListColumnKey.reviewerName,
		name: "Reviewer",
		tooltip: "Reviewer of App",
		defaultShow: false,
		isSortable: true,
		minWidth: 150,
		maxWidth: 250,
		width: 150,
	},
	{
		key: AppListColumnKey.reviewedOn,
		name: "Reviewed On",
		tooltip: "Date App was reviewed", // Last reviewed?
		defaultShow: false,
		isSortable: true,
		minWidth: 125,
		maxWidth: 125,
		width: 125,
	},
	{
		key: AppListColumnKey.comment,
		name: "Comment",
		tooltip: "Comment",
		defaultShow: false,
		isSortable: true,
		minWidth: 150,
		maxWidth: 450,
		width: 250,
	},
];

export const AdminOnlyColumnDefs: IColumnDefinition[] = [
	{
		key: AppListColumnKey.actionLinks,
		name: "Actions",
		tooltip: "Actions",
		defaultShow: true,
		isSortable: false,
		width: 150,
		minWidth: 150,
		maxWidth: 400,
	},
];

export const AppListAdminColumnDefs: IColumnDefinition[] = AppListColumnDefs.concat(AdminOnlyColumnDefs);

export function getConnectorAppColumnTitle(
	connectorApp: IConnectorAppListItem,
	column: IColumnDefinition,
): string {
	switch (column.key) {
		case AppListColumnKey.id:
			return connectorApp.id.toString();
		case AppListColumnKey.trackId:
			return connectorApp.trackId ? connectorApp.trackId.toString() : "";
		case AppListColumnKey.name:
			return connectorApp.name ? connectorApp.name : "";
		case AppListColumnKey.reviewStatusName:
			return connectorApp.reviewStatus.name ?? "";
		case AppListColumnKey.reviewerName:
			return connectorApp.reviewer?.name ?? "";
		case AppListColumnKey.reviewedOn:
			return connectorApp.reviewedOn ? new Date(connectorApp.reviewedOn).toLocaleDateString() : "";
		case AppListColumnKey.comment:
			return connectorApp.comment ?? "";
		case AppListColumnKey.clientAppId:
			return connectorApp.clientAppId?.toString() ?? "";
		case AppListColumnKey.appCategories:
			return connectorApp.appCategories
				? connectorApp.appCategories.map((category) => category.categoryName).join(", ")
				: "";
		case AppListColumnKey.version:
			return connectorApp.version?.toString() ?? "";
		default:
			return "";
	}
}

export function getConnectorAppColumnText(
	connectorApp: IConnectorAppListItem,
	column: IColumnDefinition,
): string {
	switch (column.key) {
		case AppListColumnKey.id:
			return connectorApp.id.toString();
		case AppListColumnKey.trackId:
			return connectorApp.trackId ?? "";
		case AppListColumnKey.name:
			return connectorApp.name ?? "";
		case AppListColumnKey.reviewStatusName:
			return connectorApp.reviewStatus.name ?? "";
		case AppListColumnKey.reviewerName:
			return connectorApp.reviewer?.name ?? "";
		case AppListColumnKey.reviewedOn:
			return connectorApp.reviewedOn ? new Date(connectorApp.reviewedOn).toLocaleDateString() : "";
		case AppListColumnKey.comment:
			return connectorApp.comment ?? "";
		case AppListColumnKey.clientAppId:
			return connectorApp.clientAppId?.toString() ?? "";
		case AppListColumnKey.version:
			return connectorApp.version?.toString() ?? "";
		default:
			return "";
	}
}

export const OrgListConnectorsColumnDefs: IColumnDefinition[] = [
	{ ...IdColumn, key: OrgListConnectorsColumnKey.Id, width: 75 },
	{ ...NameColumn, key: OrgListConnectorsColumnKey.Name, name: "Name", width: 175 },
	{
		key: OrgListConnectorsColumnKey.TrackId,
		name: "IVD ID",
		tooltip: "Organization Track ID",
		defaultShow: false,
		isSortable: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100,
	},
	{
		key: OrgListConnectorsColumnKey.ReviewStatusName,
		name: "Review Status",
		tooltip: "Review Status of Organization",
		defaultShow: false,
		isSortable: true,
		width: 140,
		minWidth: 140,
		maxWidth: 140,
	},
	{
		key: OrgListConnectorsColumnKey.ReviewerName,
		name: "Reviewer",
		tooltip: "Name of Organization Reviewer",
		defaultShow: false,
		isSortable: true,
		width: 150,
		minWidth: 150,
		maxWidth: 250,
	},
	{
		key: OrgListConnectorsColumnKey.ReviewedOn,
		name: "Reviewed On",
		tooltip: "Date Organization was Reviewed",
		defaultShow: false,
		isSortable: true,
		width: 125,
		minWidth: 125,
		maxWidth: 125,
	},
	{
		key: OrgListConnectorsColumnKey.Comment,
		name: "Comment",
		tooltip: "Comment",
		defaultShow: false,
		isSortable: true,
		width: 250,
		minWidth: 250,
		maxWidth: 450,
	},
	{
		key: OrgListConnectorsColumnKey.Apps,
		name: "Apps",
		tooltip: "Apps",
		defaultShow: true,
		width: 175,
		minWidth: 150,
		maxWidth: 350,
	},
	{
		key: OrgListConnectorsColumnKey.Website,
		name: "Website",
		tooltip: "Website",
		defaultShow: true,
		isSortable: true,
		width: 125,
		minWidth: 125,
		maxWidth: 250,
	},
	{ ...SlgIdColumn, key: OrgListConnectorsColumnKey.EpicCommunityMemberId, tooltip: "SLG ID", width: 75 },
	{ ...OrganizationSourceColumn, key: OrgListConnectorsColumnKey.CreatedFromName, width: 100 },
	{
		key: OrgListConnectorsColumnKey.Aliases,
		name: "Aliases",
		tooltip: "Aliases of Organization",
		defaultShow: true,
		isSortable: false,
		width: 125,
		minWidth: 125,
		maxWidth: 300,
	},
	{
		...LinksColumn,
		key: OrgListConnectorsColumnKey.ActionLinks,
		width: 150,
		name: "Actions",
		tooltip: "Actions",
		hideInPicker: false,
	},
	{
		...OrganizationStatusColumn,
		key: OrgListConnectorsColumnKey.OrganizationStatus,
		name: "VS Organization Status",
		tooltip: "Vendor Services Organization Status",
		width: 140,
		defaultShow: false,
	},
	{
		...USCDIOrganizationStatusColumn,
		key: OrgListConnectorsColumnKey.USCDIOrganizationStatus,
		width: 140,
		defaultShow: false,
	},
];

export function getConnectorsOrganizationText(
	org: IConnectorsOrganizationListItem,
	key: OrgListConnectorsColumnKey,
): string {
	switch (key) {
		case OrgListConnectorsColumnKey.Name:
			return org.name;
		case OrgListConnectorsColumnKey.Id:
			return org.id.toString();
		case OrgListConnectorsColumnKey.Website:
			return org.website || "";
		case OrgListConnectorsColumnKey.ReviewedOn:
			return org.reviewedOn ? new Date(org.reviewedOn).toLocaleDateString() : "";
		case OrgListConnectorsColumnKey.EpicCommunityMemberId:
			return org.epicCommunityMember_Id > 0 ? org.epicCommunityMember_Id.toString() : "";
		case OrgListConnectorsColumnKey.Comment:
			return org.comment;
		case OrgListConnectorsColumnKey.ReviewStatusName:
			return org.reviewStatusName;
		case OrgListConnectorsColumnKey.ReviewerName:
			return org.reviewerName;
		case OrgListConnectorsColumnKey.CreatedFromName:
			return org.createdFromName;
		case OrgListConnectorsColumnKey.TrackId:
			return org.trackId;
		case OrgListConnectorsColumnKey.Aliases:
			return org.aliases?.join(", ") ?? "";
		case OrgListConnectorsColumnKey.OrganizationStatus:
			return getStringForOrganizationStatus(org.organizationStatus);
		case OrgListConnectorsColumnKey.USCDIOrganizationStatus:
			return getStringForOrganizationStatus(org.uscdiOrganizationStatus);
	}
	return "";
}
